import {
  Button,
  Text,
  H1,
  Span,
  Input,
  EmailIcon,
  RepeatIcon,
  LoadingDots,
} from '@/components/atoms'
import { AppHeader } from '@/components/molecules'
import { useTranslation } from 'react-i18next'
import { useLoginPage } from './page.login.hook'
import { KEYBOARD_KEY } from '@/shared/constants/keyboard'
import { useLanguage } from '@/shared/lib/i18n'
import { logoHorizontal, loginImage } from '@/shared/assets'

export function LoginPage() {
  const { t } = useTranslation()
  const { switchLabel, toggleLanguage } = useLanguage()
  const {
    email,
    setEmail,
    isEmailValid,
    error,
    loadingSend,
    loadingMagicLink,
    loadingSessionToken,
    handleEmailSubmit,
  } = useLoginPage()

  if (loadingMagicLink || loadingSessionToken) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          zIndex: 9999,
        }}
      >
        <img src={logoHorizontal} width={180} height={60} alt="Kanguro" />
        <LoadingDots className="loading-md" />
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          }}
        >
          <Text className="text-base">{t('login.signingIn')}</Text>
          <Text className="text-sm text-indigo-500">
            {t('login.signingInSubtext')}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-full flex-col">
      <AppHeader variant="auth" />

      <div className="flex flex-1 flex-col-reverse overflow-hidden md:flex-1 md:flex-row md:overflow-x-auto">
        {/* Reserved space for image (not yet defined). Hidden on mobile. */}
        <div className="relative z-0 flex h-133 flex-1 items-center justify-center md:h-auto">
          <img
            src={loginImage}
            width={445}
            height={535}
            alt="Kanguro"
            className="absolute top-0 right-[calc(50dvw-95px)] object-cover md:relative md:right-0"
          />
        </div>

        <div className="flex items-start px-6 py-10 md:flex-1 md:items-center md:px-16">
          <div className="z-10 flex w-full flex-col items-start gap-4 bg-white md:max-w-125">
            <div className="flex flex-col gap-4">
              <H1>
                {t('login.title')}{' '}
                <Span textSize="" className="text-indigo-base">
                  {t('login.titleHighlight')}
                </Span>
              </H1>
              <div className="flex flex-col">
                <Text className="text-xl leading-6">
                  {t('login.description')}
                </Text>
                <Text className="text-xl leading-6">
                  {t('login.smallDescription')}
                </Text>
              </div>
            </div>

            <Button
              variant="link"
              trailingIcon={RepeatIcon}
              onClick={toggleLanguage}
              className="min-h-10.5"
            >
              {switchLabel}
            </Button>

            <Input
              id="email"
              type="email"
              label={t('login.email.label')}
              aria-label="Email"
              data-testid="email-input"
              placeholder={t('login.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) =>
                e.key === KEYBOARD_KEY.ENTER &&
                isEmailValid &&
                handleEmailSubmit()
              }
              disabled={loadingSend}
              error={error}
              icon={<EmailIcon className="h-5 w-5 text-neutral-300" />}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />

            <Button
              variant="primary"
              onClick={() => handleEmailSubmit()}
              disabled={loadingSend || !isEmailValid}
              loading={loadingSend}
              className="w-full md:max-w-60"
              data-testid="send-button"
            >
              {t('common.send')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
